'use client';

import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { ReactNode } from 'react';
import { ExternalToast, toast } from 'sonner';

type TemplateType = 'default' | 'success' | 'error' | 'warning';
interface TemplateProps {
    title: string;
    desc?: ReactNode | ReactNode[];
}

/**
 * Component sonner with template
 */
export const sonnerWithTemplate = (
    props: TemplateProps & {
        /**
         * @default 'default'
         */
        type?: TemplateType;
    },
    options?: ExternalToast
) => {
    const { type = 'default', ...rest } = props;

    switch (type) {
        case 'success':
            toast.success(template_success(rest), options);
            break;
        case 'warning':
            toast.warning(template_warning(rest), options);
            break;
        case 'error':
            toast.error(template_error(rest), options);
            break;
        default:
            toast.info(template_default(rest), options);
            break;
    }
};

export { sonnerWithTemplate as sonnerV2 };

/**create default template */
const defaultTemplate = (
    type: TemplateType,
    { title, desc }: TemplateProps
) => {
    let icon: ReactNode;

    switch (type) {
        case 'success':
            icon = (
                <CircleCheck
                    size={22}
                    strokeWidth={2}
                    className='text-green-600'
                />
            );
            break;

        case 'warning':
            icon = (
                <TriangleAlert
                    size={22}
                    strokeWidth={2}
                    className='text-amber-500'
                />
            );
            break;
        case 'error':
            icon = (
                <CircleAlert
                    size={22}
                    strokeWidth={2}
                    className='text-red-500'
                />
            );
            break;

        case 'default':
        default:
            icon = <Info size={22} strokeWidth={2} className='text-blue-500' />;
            break;
    }

    return (
        <div className='flex items-stretch gap-x-2'>
            <div>{icon}</div>
            <div className='flex-1'>
                <p className='text-sm font-medium'>{title}</p>
                {typeof desc !== 'undefined' && (
                    <>
                        {Array.isArray(desc) ? (
                            <div className='ml-1 mt-1 flex flex-col text-gray-600'>
                                {desc.map((item, index) =>
                                    typeof item === 'string' ? (
                                        <span
                                            key={index}
                                            className='line-clamp-2'
                                        >
                                            {item}
                                        </span>
                                    ) : (
                                        <>{item}</>
                                    )
                                )}
                            </div>
                        ) : typeof desc === 'string' ? (
                            <p className='mt-1 line-clamp-2 text-gray-600'>
                                {desc}
                            </p>
                        ) : (
                            desc
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Template thông báo default
 * @description Using for sonner function
 * */
const template_default = (rest: TemplateProps) => {
    return defaultTemplate('default', rest);
};

/**
 * Template thông báo thành công
 * @description Using for sonner function
 * */
const template_success = (rest: TemplateProps) => {
    return defaultTemplate('success', rest);
};

/**
 * Template cảnh báo
 * @description Using for sonner function
 * */
const template_warning = (rest: TemplateProps) => {
    return defaultTemplate('warning', rest);
};

/**
 * Template thông báo lỗi
 * @description Using for sonner function
 * */
const template_error = (rest: TemplateProps) => {
    return defaultTemplate('error', rest);
};

export { template_default, template_error, template_success, template_warning };
